/**
 * WeeklyBillsPlanner.jsx
 * 
 * A comprehensive desktop React component for weekly financial planning and bill management.
 * Uses Firestore for data persistence instead of localStorage.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase/config';
import { signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import '../styles/WeeklyBillsPlanner.css';

const WeeklyBillsPlanner = ({ userId, workspaceId }) => {
  console.log('WeeklyBillsPlanner rendered:', { userId, workspaceId });

  // Simple state management (no complex hooks)
  const [currentWeekId, setCurrentWeekId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Week data state
  const [weekDates, setWeekDates] = useState([]);
  const [dailyRevenue, setDailyRevenue] = useState({});
  const [weeklyPayroll, setWeeklyPayroll] = useState(0);
  const [startingBalance, setStartingBalance] = useState(0);
  const [purchases, setPurchases] = useState({});
  const [customProfitTax, setCustomProfitTax] = useState({});
  
  // Global data state
  const [bills, setBills] = useState([]);
  const [skippedBills, setSkippedBills] = useState({});

  // UI-only local states (data states now come from hooks)
  const [startDate, setStartDate] = useState('');
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [csvUploaded, setCsvUploaded] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showAddBillDialog, setShowAddBillDialog] = useState(false);
  const [newBill, setNewBill] = useState({ name: '', dueDay: 1, amount: '', frequency: 'Monthly' });
  const [editingBill, setEditingBill] = useState(null);
  const [editingBillOriginalAmount, setEditingBillOriginalAmount] = useState(null);
  const [moveBillInfo, setMoveBillInfo] = useState(null);
  const [showAddPurchaseDialog, setShowAddPurchaseDialog] = useState(false);
  const [newPurchase, setNewPurchase] = useState({ name: '', amount: '', date: '' });
  const [showBillsManager, setShowBillsManager] = useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const [editingBillData, setEditingBillData] = useState(null);
  
  // Auto-save with debouncing
  const saveTimeoutRef = useRef(null);

  // Simple Firebase operations
  const loadWeekData = async (weekId) => {
    if (!weekId || !workspaceId) return;
    
    setLoading(true);
    try {
      console.log('🔄 Loading week data for:', weekId);
      
      // Try to load existing data
      const metadataDoc = await getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_metadata`));
      
      if (metadataDoc.exists()) {
        // Load existing week data
        const data = metadataDoc.data();
        // Parse dates safely without timezone conversion
        const dates = data.weekDates?.map(dateStr => {
          const datePart = dateStr.split('T')[0];
          const [year, month, day] = datePart.split('-').map(Number);
          return new Date(year, month - 1, day);
        }) || [];
        
        setWeekDates(dates);
        setWeeklyPayroll(data.weeklyPayroll || 0);
        setStartingBalance(data.startingBalance || 0);
        
        // Load other data collections
        const [revenueDoc, purchasesDoc, settingsDoc] = await Promise.all([
          getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_revenue`)),
          getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_purchases`)),
          getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_settings`))
        ]);
        
        setDailyRevenue(revenueDoc.exists() ? revenueDoc.data().dailyRevenue || {} : {});
        setPurchases(purchasesDoc.exists() ? purchasesDoc.data().purchases || {} : {});
        setCustomProfitTax(settingsDoc.exists() ? settingsDoc.data().customProfitTax || {} : {});
        
        console.log('📅 Week data loaded:', { weekId, dates: dates.map(d => d.toDateString()) });
      } else {
        // Create new week
        console.log('🆕 Creating new week:', weekId);
        const dates = createWeekDatesArray(weekId);
        
        // Save to Firebase with timezone-safe dates
        await setDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_metadata`), {
          weekDates: dates.map(date => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}T00:00:00.000Z`;
          }),
          weeklyPayroll: 0,
          startingBalance: 0,
          createdAt: new Date()
        });
        
        setWeekDates(dates);
        setDailyRevenue({});
        setPurchases({});
        setCustomProfitTax({});
        setWeeklyPayroll(0);
        setStartingBalance(0);
        
        console.log('✅ New week created:', { weekId, dates: dates.map(d => d.toDateString()) });
      }
    } catch (error) {
      console.error('❌ Error loading week:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveWeekData = async () => {
    if (!currentWeekId || !workspaceId || isSaving) return;
    
    setIsSaving(true);
    try {
      console.log('💾 Saving week data for:', currentWeekId);
      
      await Promise.all([
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${currentWeekId}_metadata`), {
          weekDates: weekDates.map(date => {
            // Save as YYYY-MM-DD format to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}T00:00:00.000Z`;
          }),
          weeklyPayroll,
          startingBalance,
          lastUpdated: new Date()
        }),
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${currentWeekId}_revenue`), {
          dailyRevenue,
          lastUpdated: new Date()
        }),
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${currentWeekId}_purchases`), {
          purchases,
          lastUpdated: new Date()
        }),
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${currentWeekId}_settings`), {
          customProfitTax,
          lastUpdated: new Date()
        })
      ]);
      
      console.log('✅ Week data saved successfully:', currentWeekId);
      setLastSaved(new Date());
    } catch (error) {
      console.error('❌ Error saving week data:', error);
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const loadGlobalData = async () => {
    if (!workspaceId) return;
    
    try {
      const [billsDoc, skippedDoc] = await Promise.all([
        getDoc(doc(db, `workspaces/${workspaceId}/data/bills`)),
        getDoc(doc(db, `workspaces/${workspaceId}/data/skippedBills`))
      ]);
      
      setBills(billsDoc.exists() ? billsDoc.data().bills || [] : []);
      setSkippedBills(skippedDoc.exists() ? skippedDoc.data().skippedBills || {} : {});
    } catch (error) {
      console.error('❌ Error loading global data:', error);
      setError(error.message);
    }
  };

  const updateBills = async (newBills) => {
    try {
      await setDoc(doc(db, `workspaces/${workspaceId}/data/bills`), {
        bills: newBills,
        lastUpdated: new Date()
      });
      setBills(newBills);
    } catch (error) {
      console.error('❌ Error updating bills:', error);
    }
  };

  const updateSkippedBills = async (newSkippedBills) => {
    try {
      await setDoc(doc(db, `workspaces/${workspaceId}/data/skippedBills`), {
        skippedBills: newSkippedBills,
        lastUpdated: new Date()
      });
      setSkippedBills(newSkippedBills);
    } catch (error) {
      console.error('❌ Error updating skipped bills:', error);
    }
  };

  // Helper functions for week data updates
  const updateDailyRevenue = (dateStr, amount) => {
    setDailyRevenue(prev => ({
      ...prev,
      [dateStr]: parseFloat(amount) || 0
    }));
  };

  const updateWeeklyPayroll = (amount) => {
    setWeeklyPayroll(parseFloat(amount) || 0);
  };

  const updateStartingBalance = (amount) => {
    setStartingBalance(parseFloat(amount) || 0);
  };

  const addPurchase = (dateStr, purchase) => {
    setPurchases(prev => ({
      ...prev,
      [dateStr]: [...(prev[dateStr] || []), purchase]
    }));
  };

  const removePurchase = (dateStr, purchaseId) => {
    setPurchases(prev => ({
      ...prev,
      [dateStr]: (prev[dateStr] || []).filter(p => p.id !== purchaseId)
    }));
  };

  const updateCustomProfitTaxForDate = (dateStr, settings) => {
    setCustomProfitTax(prev => ({
      ...prev,
      [dateStr]: settings
    }));
  };

  // Auto-save week data when it changes (but not when switching weeks)
  useEffect(() => {
    // Don't auto-save if we don't have a current week or if week dates are empty
    if (!currentWeekId || weekDates.length === 0) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounced save - but skip auto-save during week transitions
    saveTimeoutRef.current = setTimeout(() => {
      // Double-check that we still have valid data before saving
      if (currentWeekId && weekDates.length > 0 && !loading) {
        saveWeekData();
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [dailyRevenue, weeklyPayroll, startingBalance, purchases, customProfitTax]); // Removed currentWeekId and weekDates to prevent saves during navigation

  // Initialize with current week when component loads
  useEffect(() => {
    if (!currentWeekId) {
      console.log('🏠 Initializing to current week (desktop)...');
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      
      // Get Sunday of current week
      const sundayDateStr = findSundayOfWeek(todayStr);
      
      setCurrentWeekId(sundayDateStr);
    }
  }, []);

  // Load global data when workspace changes
  useEffect(() => {
    if (workspaceId) {
      loadGlobalData();
    }
  }, [workspaceId]);

  // Load week data when currentWeekId changes
  useEffect(() => {
    if (currentWeekId) {
      loadWeekData(currentWeekId);
    }
  }, [currentWeekId]);

  // Set CSV uploaded status based on bills
  useEffect(() => {
    if (bills && bills.length > 0) {
      setCsvUploaded(true);
    }
  }, [bills]);





  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Generate week ID from start date (YYYY-MM-DD format)
  const getWeekId = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };





  // Navigate to previous week - with manual save
  const goToPreviousWeek = async () => {
    if (!currentWeekId) return;
    
    try {
      // Save current week data before switching
      if (currentWeekId && weekDates.length > 0) {
        console.log('Saving current week before navigating to previous week');
        await saveWeekData();
      }
      
      // Parse current week ID and subtract 7 days
      const [year, month, day] = currentWeekId.split('-').map(Number);
      const currentSunday = new Date(year, month - 1, day);
      const previousSunday = new Date(currentSunday);
      previousSunday.setDate(currentSunday.getDate() - 7);
      
      const prevYear = previousSunday.getFullYear();
      const prevMonth = String(previousSunday.getMonth() + 1).padStart(2, '0');
      const prevDay = String(previousSunday.getDate()).padStart(2, '0');
      const sundayDateStr = `${prevYear}-${prevMonth}-${prevDay}`;
      
      console.log('Navigating to previous week:', sundayDateStr);
      setCurrentWeekId(sundayDateStr);
    } catch (error) {
      console.error('Error navigating to previous week:', error);
    }
  };

  // Navigate to next week - with manual save
  const goToNextWeek = async () => {
    if (!currentWeekId) return;
    
    try {
      // Save current week data before switching
      if (currentWeekId && weekDates.length > 0) {
        console.log('Saving current week before navigating to next week');
        await saveWeekData();
      }
      
      // Parse current week ID and add 7 days
      const [year, month, day] = currentWeekId.split('-').map(Number);
      const currentSunday = new Date(year, month - 1, day);
      const nextSunday = new Date(currentSunday);
      nextSunday.setDate(currentSunday.getDate() + 7);
      
      const nextYear = nextSunday.getFullYear();
      const nextMonth = String(nextSunday.getMonth() + 1).padStart(2, '0');
      const nextDay = String(nextSunday.getDate()).padStart(2, '0');
      const sundayDateStr = `${nextYear}-${nextMonth}-${nextDay}`;
      
      console.log('Navigating to next week:', sundayDateStr);
      setCurrentWeekId(sundayDateStr);
    } catch (error) {
      console.error('Error navigating to next week:', error);
    }
  };

  // Find the Sunday (start of week) for any given date
  const findSundayOfWeek = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - dayOfWeek);
    const sundayYear = sunday.getFullYear();
    const sundayMonth = String(sunday.getMonth() + 1).padStart(2, '0');
    const sundayDay = String(sunday.getDate()).padStart(2, '0');
    return `${sundayYear}-${sundayMonth}-${sundayDay}`;
  };

  // Create array of dates for a week starting from Sunday
  const createWeekDatesArray = (sundayDateStr) => {
    const [year, month, day] = sundayDateStr.split('-').map(Number);
    const sunday = new Date(year, month - 1, day);
    const dates = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday);
      date.setDate(sunday.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };



  // Generate week dates starting from the selected date - simplified with new hooks
  const generateWeekDates = (startDateStr) => {
    // Always start from Sunday of the selected week
    const sundayDateStr = findSundayOfWeek(startDateStr);
    
    // Set the current week ID - the useWeekData hook will handle the rest
    setCurrentWeekId(sundayDateStr);
    setShowDateDialog(false);
  };

  // Handle setting start date from dialog - simplified with new hooks
  const handleSetStartDate = async () => {
    if (!startDate) return;
    
    // Get Sunday of the selected week using timezone-safe parsing
    const sundayDateStr = findSundayOfWeek(startDate);
    
    setCurrentWeekId(sundayDateStr);
    setShowDateDialog(false);
  };

  // Handle CSV file upload (adapted for Firestore)
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const content = e.target.result;
        const lines = content.split('\n');
        const headers = lines[0].split(',');
        
        const nameIndex = headers.findIndex(h => h.trim() === 'Name');
        const dueDayIndex = headers.findIndex(h => h.trim() === 'Due Day');
        const amountIndex = headers.findIndex(h => h.trim() === 'Amount');
        const frequencyIndex = headers.findIndex(h => h.trim() === 'Frequency');
        
        const parsedBills = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          let values = [];
          let inQuote = false;
          let currentValue = '';
          
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            
            if (char === '"') {
              inQuote = !inQuote;
            } else if (char === ',' && !inQuote) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          
          values.push(currentValue.trim());
          
          if (values.length < Math.max(nameIndex, dueDayIndex, amountIndex, frequencyIndex) + 1) {
            continue;
          }
          
          const name = values[nameIndex]?.trim();
          const dueDayRaw = values[dueDayIndex]?.trim();
          const dueDay = parseInt(dueDayRaw);
          
          let amount = values[amountIndex]?.trim() || '0';
          amount = amount.replace(/^\$|\s+/g, '');
          amount = parseFloat(amount.replace(/,/g, ''));
          
          const frequency = values[frequencyIndex]?.trim();
          
          if (name && !isNaN(amount)) {
            parsedBills.push({
              id: Date.now() + i,
              name,
              dueDay: isNaN(dueDay) ? 1 : dueDay,
              amount,
              frequency,
              rawDueDay: dueDayRaw
            });
          }
        }
        
        await updateBills(parsedBills);
        setCsvUploaded(true);
      };
      
      reader.readAsText(file);
    }
  };

  // Update daily revenue - use hook function
  const handleDailyRevenueChange = (date, value) => {
    const dateStr = date.toISOString().split('T')[0];
    updateDailyRevenue(dateStr, value);
  };

  // Update starting balance
  const handleStartingBalanceChange = (value) => {
    updateStartingBalance(value);
  };

  // Calculate total revenue for the week
  const calculateTotalRevenue = () => {
    return Object.values(dailyRevenue).reduce((sum, revenue) => sum + revenue, 0);
  };

  // Handle custom profit settings - use hook function
  const toggleCustomProfit = async (dateStr) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: false, profitAmount: 10, useCustomTax: false, taxAmount: 10 };
    const updated = { ...current, useCustomProfit: !current.useCustomProfit };
    updateCustomProfitTaxForDate(dateStr, updated);
  };

  // Handle custom tax settings - use hook function
  const toggleCustomTax = async (dateStr) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: false, profitAmount: 10, useCustomTax: false, taxAmount: 10 };
    const updated = { ...current, useCustomTax: !current.useCustomTax };
    updateCustomProfitTaxForDate(dateStr, updated);
  };

    // Handle custom profit amount change - use hook function
  const handleCustomProfitChange = async (dateStr, value) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: true, profitAmount: 10, useCustomTax: false, taxAmount: 10 };
    const updated = { ...current, profitAmount: parseFloat(value) || 0 };
    updateCustomProfitTaxForDate(dateStr, updated);
  };

    // Handle custom tax amount change - use hook function
  const handleCustomTaxChange = async (dateStr, value) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: false, profitAmount: 10, useCustomTax: true, taxAmount: 10 };
    const updated = { ...current, taxAmount: parseFloat(value) || 0 };
    updateCustomProfitTaxForDate(dateStr, updated);
  };

  // Calculate profit for a specific date (same as original)
  const calculateProfitForDate = (date) => {
    if (!(date instanceof Date)) return 0;
    
    const dateStr = date.toISOString().split('T')[0];
    const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
    const isSunday = dayName === 'Sunday';
    
    if (isSunday) return 0;
    
    const revenue = dailyRevenue[dateStr] || 0;
    
    // Check if we should use custom profit settings
    if (customProfitTax[dateStr] && customProfitTax[dateStr].useCustomProfit) {
      const profitPercent = customProfitTax[dateStr].profitAmount;
      return revenue * (profitPercent / 100);
    }
    
    // Default is 10%
    return revenue * 0.1;
  };

  // Calculate tax for a specific date (same as original)
  const calculateTaxForDate = (date) => {
    if (!(date instanceof Date)) return 0;
    
    const dateStr = date.toISOString().split('T')[0];
    const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
    const isSunday = dayName === 'Sunday';
    
    if (isSunday) return 0;
    
    const revenue = dailyRevenue[dateStr] || 0;
    
    // Check if we should use custom tax settings
    if (customProfitTax[dateStr] && customProfitTax[dateStr].useCustomTax) {
      const taxPercent = customProfitTax[dateStr].taxAmount;
      return revenue * (taxPercent / 100);
    }
    
    // Default is 10%
    return revenue * 0.1;
  };

  // Get bills due for a specific date (same as original)
  const getBillsForDate = (date) => {
    if (!(date instanceof Date)) {
      console.error('Invalid date object:', date);
      return [];
    }
    
    const day = date.getDate();
    const dayOfWeek = date.toLocaleDateString(undefined, { weekday: 'long' });
    const dateStr = date.toISOString().split('T')[0];
    
    if (!bills || !Array.isArray(bills)) {
      return [];
    }
    
    return bills.filter(bill => {
      // Skip bills that are marked as skipped for this date
      if (skippedBills[dateStr] && skippedBills[dateStr].includes(bill.id)) {
        return false;
      }
      
      // Special handling for Virtustant (weekly on Tuesday)
      const isVirtustant = bill.name === 'Virtustant';
      const isWeeklyTuesday = bill.frequency && 
                            (bill.frequency.toLowerCase().includes('every tuesday') || 
                             bill.frequency.toLowerCase().includes('tuesday'));
      
      // Weekly bill occurs on the right day of week
      if ((isVirtustant || isWeeklyTuesday) && dayOfWeek === 'Tuesday') {
        return true;
      }
      
      // Handle monthly bills by comparing the due day with the date
      return bill.dueDay === day;
    });
  };

  // Get purchases for a specific date (same as original)
  const getPurchasesForDate = (date) => {
    if (!(date instanceof Date)) return [];
    
    const dateStr = date.toISOString().split('T')[0];
    const weekPurchases = purchases[currentWeekId] || {};
    return weekPurchases[dateStr] || [];
  };

  // Calculate total purchases for a date (same as original)
  const calculateDatePurchasesTotal = (date) => {
    const datePurchases = getPurchasesForDate(date);
    return datePurchases.reduce((sum, purchase) => sum + purchase.amount, 0);
  };

  // Calculate total purchases for the week (same as original)
  const calculateTotalPurchases = () => {
    let total = 0;
    weekDates.forEach(date => {
      total += calculateDatePurchasesTotal(date);
    });
    return total;
  };

  // Calculate total bills for the week (same as original)
  const calculateTotalBills = () => {
    let total = 0;
    weekDates.forEach(date => {
      const dateBills = getBillsForDate(date);
      total += dateBills.reduce((sum, bill) => sum + bill.amount, 0);
    });
    return total;
  };

  // Calculate balance forward for a specific day (same as original)
  const calculateBalanceForward = (dayIndex) => {
    if (dayIndex === 0) {
      // Sunday's balance forward is the starting balance
      return startingBalance;
    }
    
    // For other days, calculate the ending balance of the previous day
    const prevIndex = dayIndex - 1;
    const prevDate = weekDates[prevIndex];
    const prevDayName = prevDate.toLocaleDateString(undefined, { weekday: 'long' });
    
    // Get bills and purchases for previous date
    const prevDateStr = prevDate.toISOString().split('T')[0];
    const prevBills = getBillsForDate(prevDate);
    const prevBillsTotal = prevBills.reduce((sum, bill) => sum + bill.amount, 0);
    const prevPurchasesTotal = calculateDatePurchasesTotal(prevDate);
    
    // Get revenue, profit, and taxes for previous date
    const prevIsSunday = prevDayName === 'Sunday';
    const prevRevenue = prevIsSunday ? 0 : (dailyRevenue[prevDateStr] || 0);
    const prevProfit = calculateProfitForDate(prevDate);
    const prevTaxes = calculateTaxForDate(prevDate);
    
    // Get previous day's balance forward
    const prevBalanceForward = calculateBalanceForward(prevIndex);
    
    // Calculate previous day's ending balance
    let prevBalance = prevBalanceForward - prevBillsTotal - prevPurchasesTotal + prevRevenue - prevProfit - prevTaxes;
    
    // Special handling for Friday (payroll deduction)
    if (prevDayName === 'Friday') {
      prevBalance -= parseFloat(weeklyPayroll) || 0;
    }
    
    return prevBalance;
  };

  // Calculate intermediate balance after bills and purchases but before revenue (same as original)
  const calculateIntermediateBalance = (date, balanceForward) => {
    if (!(date instanceof Date)) return balanceForward;
    
    // Get bills and purchases for this date
    const dateBills = getBillsForDate(date);
    const billsTotal = dateBills.reduce((sum, bill) => sum + bill.amount, 0);
    const purchasesTotal = calculateDatePurchasesTotal(date);
    
    // Subtract bills and purchases from balance forward
    return balanceForward - billsTotal - purchasesTotal;
  };

  // Calculate the new balance forward (ending balance) for a day (same as original)
  const calculateNewBalanceForward = (date, intermediateBalance) => {
    if (!(date instanceof Date)) return intermediateBalance;
    
    const dateStr = date.toISOString().split('T')[0];
    const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
    const isSunday = dayName === 'Sunday';
    
    // Get revenue, profit, and taxes for this date
    const revenue = isSunday ? 0 : (dailyRevenue[dateStr] || 0);
    const profit = calculateProfitForDate(date);
    const taxes = calculateTaxForDate(date);
    
    // Add revenue and subtract profit and taxes
    let newBalance = intermediateBalance + revenue - profit - taxes;
    
    // Special handling for Friday (payroll deduction)
    if (dayName === 'Friday') {
      newBalance -= parseFloat(weeklyPayroll) || 0;
    }
    
    return newBalance;
  };

  // Calculate total profit for the week (same as original)
  const calculateTotalProfit = () => {
    if (!weekDates.length) return 0;
    
    let totalProfit = 0;
    weekDates.forEach(date => {
      totalProfit += calculateProfitForDate(date);
    });
    
    return totalProfit;
  };

  // Calculate total taxes for the week (same as original)
  const calculateTotalTaxes = () => {
    if (!weekDates.length) return 0;
    
    let totalTaxes = 0;
    weekDates.forEach(date => {
      totalTaxes += calculateTaxForDate(date);
    });
    
    return totalTaxes;
  };

  // Calculate final balance forward (end of week) (same as original)
  const calculateFinalBalanceForward = () => {
    if (!weekDates.length) return 0;
    
    // Get the last day of the week (typically Saturday)
    const lastDay = weekDates[weekDates.length - 1];
    const lastDayIndex = weekDates.length - 1;
    
    // Calculate intermediate balance for the last day
    const balanceForward = calculateBalanceForward(lastDayIndex);
    const intermediateBalance = calculateIntermediateBalance(lastDay, balanceForward);
    
    // Return the new balance forward for the last day
    return calculateNewBalanceForward(lastDay, intermediateBalance);
  };

  // Format currency (same as original)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date for display (same as original)
  const formatDateRange = () => {
    if (weekDates.length === 0) return '';
    
    const startDate = weekDates[0];
    const endDate = weekDates[weekDates.length - 1];
    
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${startDate.toLocaleDateString(undefined, options)} - ${endDate.toLocaleDateString(undefined, options)}`;
  };

  // Handle adding a new bill (adapted for Firestore)
  const handleAddBill = async () => {
    if (newBill.name && newBill.amount) {
      const updatedBills = [...(bills || [])];
      updatedBills.push({
        id: Date.now(),
        name: newBill.name,
        dueDay: parseInt(newBill.dueDay) || 1,
        amount: parseFloat(newBill.amount) || 0,
        frequency: newBill.frequency,
        rawDueDay: newBill.dueDay.toString()
      });
      
      await updateBills(updatedBills);
      setNewBill({ name: '', dueDay: 1, amount: '', frequency: 'Monthly' });
      setShowAddBillDialog(false);
    }
  };

  // Handle removing a bill permanently (adapted for Firestore)
  const handleRemoveBill = async (billId) => {
    const updatedBills = bills.filter(bill => bill.id !== billId);
    await updateBills(updatedBills);
  };

  // Handle skipping a bill for a specific date (adapted for Firestore)
  const handleSkipBill = async (date, billId) => {
    const dateStr = date.toISOString().split('T')[0];
    const updatedSkippedBills = { ...skippedBills };
    
    if (!updatedSkippedBills[dateStr]) {
      updatedSkippedBills[dateStr] = [];
    }
    
    updatedSkippedBills[dateStr].push(billId);
    await updateSkippedBills(updatedSkippedBills);
  };

  // Handle editing a bill amount (adapted for Firestore)
  const handleEditBillAmount = async (billId, newAmount) => {
    const updatedBills = bills.map(bill => 
      bill.id === billId ? { ...bill, amount: parseFloat(newAmount) || 0 } : bill
    );
    await updateBills(updatedBills);
    setEditingBill(null);
    setEditingBillOriginalAmount(null);
  };

  // Handle canceling bill amount edit (same as original)
  const handleCancelEdit = () => {
    setEditingBill(null);
    setEditingBillOriginalAmount(null);
  };

  // Handle moving a bill to a different day (adapted for Firestore)
  const handleMoveBill = async (billId, newDueDay) => {
    const updatedBills = bills.map(bill => 
      bill.id === billId ? { ...bill, dueDay: parseInt(newDueDay), rawDueDay: newDueDay.toString() } : bill
    );
    await updateBills(updatedBills);
    setMoveBillInfo(null);
  };

  // Handle adding a purchase - use hook function
  const handleAddPurchase = async () => {
    if (newPurchase.name && newPurchase.amount && newPurchase.date) {
      const dateStr = newPurchase.date;
      
      // Make sure the date is within the current week
      const isInCurrentWeek = weekDates.some(date => 
        date.toISOString().split('T')[0] === dateStr
      );
      
      if (!isInCurrentWeek) {
        alert('Please select a date within the current week.');
        return;
      }
      
      const purchase = {
        id: Date.now(),
        name: newPurchase.name,
        amount: parseFloat(newPurchase.amount) || 0
      };
      
      addPurchase(dateStr, purchase);
      setNewPurchase({ name: '', amount: '', date: '' });
      setShowAddPurchaseDialog(false);
    }
  };

  // Handle removing a purchase - use hook function
  const handleRemovePurchase = async (date, purchaseId) => {
    const dateStr = date.toISOString().split('T')[0];
    removePurchase(dateStr, purchaseId);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h1>Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="weekly-bills-planner">
      <div className="planner-header">
        <h1>Weekly Bills Planner</h1>
        <div className="save-status">
          {isSaving && <span className="save-indicator">💾 Saving...</span>}
          {lastSaved && !isSaving && (
            <span className="save-indicator">✅ Saved {lastSaved.toLocaleTimeString()}</span>
          )}
        </div>
      </div>
      
      <div className="planner-actions">
        <button 
          onClick={() => setShowDateDialog(true)}
          className="date-button"
        >
          Set Week Start Date
        </button>
        
        <div className="file-upload">
          <label>
            {csvUploaded ? 'Update Bills CSV' : 'Upload Bills CSV'}
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
            />
          </label>
          <span className="file-status">
            {csvUploaded ? `${bills?.length || 0} bills loaded` : 'No bills loaded'}
          </span>
        </div>
        
        <div className="payroll-input">
          <label>Weekly Payroll: $</label>
          <input
            type="number"
            value={weeklyPayroll}
                              onChange={(e) => updateWeeklyPayroll(e.target.value)}
            placeholder="Enter weekly payroll amount"
          />
        </div>
        
        <button 
          onClick={() => setShowAddBillDialog(true)}
          className="add-bill-button"
        >
          Add Bill
        </button>
        
        <button 
          onClick={() => setShowAddPurchaseDialog(true)}
          className="add-purchase-button"
        >
          Add Purchase
        </button>
        
        <button 
          onClick={() => setShowDebug(!showDebug)} 
          className="debug-button"
        >
          {showDebug ? 'Hide Debug' : 'Debug Bills'}
        </button>
        
        <button 
          onClick={() => setShowBillsManager(true)}
          className="manage-bills-button"
        >
          Manage Bills
        </button>
        
        <button 
          onClick={handleLogout}
          className="logout-button"
        >
          Logout
        </button>
      </div>

      {showDebug && (
        <div className="debug-panel">
          <h3>Loaded Bills ({bills?.length || 0})</h3>
          <table className="debug-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Due Day</th>
                <th>Amount</th>
                <th>Frequency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bills?.sort((a, b) => a.dueDay - b.dueDay).map((bill, index) => (
                <tr key={index}>
                  <td>{bill.name}</td>
                  <td>{bill.dueDay} ({bill.rawDueDay})</td>
                  <td>{formatCurrency(bill.amount)}</td>
                  <td>{bill.frequency}</td>
                  <td>
                    <button onClick={() => handleRemoveBill(bill.id)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showDateDialog && (
        <div className="date-dialog">
          <div className="dialog-content">
            <h3>Select Week Start Date</h3>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <div className="dialog-buttons">
              <button onClick={() => setShowDateDialog(false)}>Cancel</button>
              <button 
                onClick={handleSetStartDate}
                disabled={!startDate}
              >
                Generate Week
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddBillDialog && (
        <div className="date-dialog">
          <div className="dialog-content">
            <h3>Add New Bill</h3>
            <div className="form-field">
              <label>Bill Name:</label>
              <input
                type="text"
                value={newBill.name}
                onChange={(e) => setNewBill({...newBill, name: e.target.value})}
                placeholder="Enter bill name"
              />
            </div>
            <div className="form-field">
              <label>Due Day (1-31):</label>
              <input
                type="number"
                min="1"
                max="31"
                value={newBill.dueDay}
                onChange={(e) => setNewBill({...newBill, dueDay: e.target.value})}
              />
            </div>
            <div className="form-field">
              <label>Amount:</label>
                <input
                  type="number"
                  step="0.01"
                  value={newBill.amount}
                  onChange={(e) => setNewBill({...newBill, amount: e.target.value})}
                  placeholder="0.00"
                />
            </div>
            <div className="form-field">
              <label>Frequency:</label>
              <select
                value={newBill.frequency}
                onChange={(e) => setNewBill({...newBill, frequency: e.target.value})}
              >
                <option value="Monthly">Monthly</option>
                <option value="Weekly">Weekly</option>
                <option value="Every Tuesday">Every Tuesday</option>
              </select>
            </div>
            <div className="dialog-buttons">
              <button onClick={() => setShowAddBillDialog(false)}>Cancel</button>
              <button 
                onClick={handleAddBill}
                disabled={!newBill.name || !newBill.amount}
              >
                Add Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPurchaseDialog && (
        <div className="date-dialog">
          <div className="dialog-content">
            <h3>Add New Purchase</h3>
            <div className="form-field">
              <label>Purchase Name:</label>
              <input
                type="text"
                value={newPurchase.name}
                onChange={(e) => setNewPurchase({...newPurchase, name: e.target.value})}
                placeholder="Enter purchase name"
              />
            </div>
            <div className="form-field">
              <label>Amount:</label>
                <input
                  type="number"
                  step="0.01"
                  value={newPurchase.amount}
                  onChange={(e) => setNewPurchase({...newPurchase, amount: e.target.value})}
                  placeholder="0.00"
                />
            </div>
            <div className="form-field">
              <label>Date:</label>
              <select
                value={newPurchase.date}
                onChange={(e) => setNewPurchase({...newPurchase, date: e.target.value})}
              >
                <option value="">Select a date</option>
                {weekDates.map((date, index) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
                  const formattedDate = date.toLocaleDateString();
                  return (
                    <option key={index} value={dateStr}>
                      {dayName} - {formattedDate}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="dialog-buttons">
              <button onClick={() => setShowAddPurchaseDialog(false)}>Cancel</button>
              <button 
                onClick={handleAddPurchase}
                disabled={!newPurchase.name || !newPurchase.amount || !newPurchase.date}
              >
                Add Purchase
              </button>
            </div>
          </div>
        </div>
      )}

      {showBillsManager && (
        <div className="bills-manager-overlay">
          <div className="bills-manager-dialog">
            <div className="bills-manager-header">
              <h2>Manage Bills</h2>
              <button 
                className="close-button"
                onClick={() => setShowBillsManager(false)}
              >
                ×
              </button>
            </div>

            <div className="bills-manager-content">
              <div className="bills-table-container">
                <table className="bills-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Due Day</th>
                      <th>Amount</th>
                      <th>Frequency</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bills?.sort((a, b) => a.dueDay - b.dueDay).map((bill) => (
                      <tr key={bill.id}>
                        {editingBillId === bill.id ? (
                          <>
                            <td>
                              <input
                                type="text"
                                value={editingBillData.name}
                                onChange={(e) => setEditingBillData({
                                  ...editingBillData,
                                  name: e.target.value
                                })}
                                className="edit-input"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                max="31"
                                value={editingBillData.dueDay}
                                onChange={(e) => setEditingBillData({
                                  ...editingBillData,
                                  dueDay: parseInt(e.target.value) || 1,
                                  rawDueDay: e.target.value
                                })}
                                className="edit-input"
                              />
                            </td>
                            <td>
                              <div className="amount-input">
                                <span>$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingBillData.amount}
                                  onChange={(e) => setEditingBillData({
                                    ...editingBillData,
                                    amount: parseFloat(e.target.value) || 0
                                  })}
                                  className="edit-input"
                                />
                              </div>
                            </td>
                            <td>
                              <select
                                value={editingBillData.frequency}
                                onChange={(e) => setEditingBillData({
                                  ...editingBillData,
                                  frequency: e.target.value
                                })}
                                className="edit-input"
                              >
                                <option value="Monthly">Monthly</option>
                                <option value="Weekly">Weekly</option>
                                <option value="Every Tuesday">Every Tuesday</option>
                              </select>
                            </td>
                            <td className="action-buttons">
                              <button
                                className="save-button"
                                onClick={async () => {
                                  const updatedBills = bills.map(b => 
                                    b.id === bill.id ? editingBillData : b
                                  );
                                  await updateBills(updatedBills);
                                  setEditingBillId(null);
                                  setEditingBillData(null);
                                }}
                              >
                                Save
                              </button>
                              <button
                                className="cancel-button"
                                onClick={() => {
                                  setEditingBillId(null);
                                  setEditingBillData(null);
                                }}
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{bill.name}</td>
                            <td>Day {bill.dueDay}</td>
                            <td>{formatCurrency(bill.amount)}</td>
                            <td>{bill.frequency}</td>
                            <td className="action-buttons">
                              <button
                                className="edit-button"
                                onClick={() => {
                                  setEditingBillId(bill.id);
                                  setEditingBillData({...bill});
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="delete-button"
                                onClick={async () => {
                                  if (window.confirm(`Delete bill "${bill.name}"?`)) {
                                    await handleRemoveBill(bill.id);
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bills-manager-footer">
                <button
                  className="add-bill-button"
                  onClick={() => {
                    setShowAddBillDialog(true);
                    setShowBillsManager(false);
                  }}
                >
                  + Add New Bill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {weekDates.length > 0 && (
        <div className="planner-content">
          <div className="week-navigation">
            <button onClick={goToPreviousWeek} className="nav-button">
              ← Previous Week
            </button>
            <h2 className="week-range">{formatDateRange()}</h2>
            <button onClick={goToNextWeek} className="nav-button">
              Next Week →
            </button>
          </div>
          
          <div className="revenue-total">
            <h2>Revenue Total: {formatCurrency(calculateTotalRevenue())}</h2>
          </div>
          
          <div className="weekly-summary">
            <div className="summary-box">
              <h3>Profit</h3>
              <p>{formatCurrency(calculateTotalProfit())}</p>
            </div>
            <div className="summary-box">
              <h3>Taxes</h3>
              <p>{formatCurrency(calculateTotalTaxes())}</p>
            </div>
            <div className="summary-box">
              <h3>Payroll</h3>
              <p>{formatCurrency(parseFloat(weeklyPayroll) || 0)}</p>
            </div>
            <div className="summary-box">
              <h3>Bills</h3>
              <p>{formatCurrency(calculateTotalBills())}</p>
            </div>
            <div className="summary-box">
              <h3>Purchases</h3>
              <p>{formatCurrency(calculateTotalPurchases())}</p>
            </div>
            <div className="summary-box">
              <h3>Balance Forward</h3>
              <p>{formatCurrency(calculateFinalBalanceForward())}</p>
            </div>
          </div>
          
          <div className="daily-breakdown">
            {weekDates.map((date, index) => {
              const dateStr = date.toISOString().split('T')[0];
              const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
              const formattedDate = date.toLocaleDateString();
              const isSunday = dayName === 'Sunday';
              const isFriday = dayName === 'Friday';
              const isTuesday = dayName === 'Tuesday';
              
              // Get bills for this date
              const dateBills = getBillsForDate(date);
              const billsTotal = dateBills.reduce((sum, bill) => sum + bill.amount, 0);
              
              // Get purchases for this date
              const datePurchases = getPurchasesForDate(date);
              const purchasesTotal = datePurchases.reduce((sum, purchase) => sum + purchase.amount, 0);
              
              // Get balance calculations
              const balanceForward = calculateBalanceForward(index);
              const intermediateBalance = calculateIntermediateBalance(date, balanceForward);
              const newBalanceForward = calculateNewBalanceForward(date, intermediateBalance);
              
              // Get revenue, profit, taxes
              const revenue = isSunday ? 0 : (dailyRevenue[dateStr] || 0);
              const profit = calculateProfitForDate(date);
              const taxes = calculateTaxForDate(date);
              
              // Get custom settings for this date
              const customSettings = customProfitTax[dateStr] || { 
                useCustomProfit: false, 
                profitAmount: 10, 
                useCustomTax: false, 
                taxAmount: 10 
              };
              
              // Handle Friday payroll calculation
              const payrollDeduction = (isFriday) ? (parseFloat(weeklyPayroll) || 0) : 0;
              const balanceAfterPayroll = isFriday ? (balanceForward - payrollDeduction) : balanceForward;
              
              return (
                <div className="day-box" key={index}>
                  <div className="day-header">
                    <h3>{dayName}</h3>
                    <span>{formattedDate}</span>
                  </div>
                  
                  <div className="day-content">
                    {isSunday && (
                      <div className="day-item">
                        <span>Starting Balance:</span>
                        <input
                          type="number"
                          value={startingBalance || ''}
                          onChange={(e) => handleStartingBalanceChange(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                    )}
                    
                    {/* Special handling for Friday and Tuesday - Display at the top */}
                    {(isFriday || isTuesday) && (
                      <div className="day-item special-day-calculation">
                        <h4>{isFriday ? 'Payroll Calculation' : 'Virtustant Calculation'}</h4>
                        <div className="special-day-breakdown">
                          <div className="breakdown-item">
                            <span>Balance Forward:</span>
                            <span>{formatCurrency(balanceForward)}</span>
                          </div>
                          
                          {isFriday && (
                            <div className="breakdown-item">
                              <span>Payroll Deduction:</span>
                              <span>- {formatCurrency(payrollDeduction)}</span>
                            </div>
                          )}
                          
                          <div className="breakdown-divider"></div>
                          <div className="breakdown-item total">
                            <span>Remaining Balance:</span>
                            <span>{formatCurrency(balanceAfterPayroll)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Daily Balance Calculation Section */}
                    <div className="balance-calculation-section">
                      <div className="section-header">
                        <h4>Balance Calculation</h4>
                      </div>
                      
                      <div className="day-item">
                        <span>Balance Forward:</span>
                        <span>{formatCurrency((isFriday || isTuesday) ? balanceAfterPayroll : balanceForward)}</span>
                      </div>
                      
                      <div className="day-item bills-list">
                        <span>Bills:</span>
                        <div>
                          {dateBills.length > 0 ? (
                            <ul>
                              {dateBills.map((bill, billIndex) => (
                                <li key={billIndex} className="bill-item">
                                  <div className="bill-item-content">
                                    {bill.name}: 
                                    {editingBill === bill.id ? (
                                      <div className="bill-edit-form">
                                        <input
                                          type="number"
                                          value={bill.amount}
                                          onChange={(e) => handleEditBillAmount(bill.id, e.target.value)}
                                          step="0.01"
                                        />
                                        <button onClick={() => setEditingBill(null)}>Save</button>
                                        <button onClick={handleCancelEdit}>Cancel</button>
                                      </div>
                                    ) : (
                                      <span 
                                        onClick={() => {
                                          setEditingBillOriginalAmount(bill.amount);
                                          setEditingBill(bill.id);
                                        }} 
                                        className="bill-amount"
                                      >
                                        {formatCurrency(bill.amount)}
                                      </span>
                                    )}
                                  </div>
                                  <div className="bill-actions">
                                    <button onClick={() => setMoveBillInfo({ id: bill.id, currentDay: bill.dueDay })} title="Move bill">
                                      Move
                                    </button>
                                    <button onClick={() => handleSkipBill(date, bill.id)} title="Skip bill for this day">
                                      Skip
                                    </button>
                                  </div>
                                  
                                  {moveBillInfo && moveBillInfo.id === bill.id && (
                                    <div className="move-bill-dialog">
                                      <select
                                        value={moveBillInfo.newDay || bill.dueDay}
                                        onChange={(e) => setMoveBillInfo({...moveBillInfo, newDay: e.target.value})}
                                      >
                                        {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                                          <option key={day} value={day}>Day {day}</option>
                                        ))}
                                      </select>
                                      <button 
                                        onClick={() => handleMoveBill(bill.id, moveBillInfo.newDay || bill.dueDay)}
                                      >
                                        Move
                                      </button>
                                      <button onClick={() => setMoveBillInfo(null)}>Cancel</button>
                                    </div>
                                  )}
                                </li>
                              ))}
                              <li className="bills-total">
                                Total: {formatCurrency(billsTotal)}
                              </li>
                            </ul>
                          ) : (
                            <span>{formatCurrency(0)}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="day-item purchases-list">
                        <span>Purchases:</span>
                        <div>
                          {datePurchases.length > 0 ? (
                            <ul>
                              {datePurchases.map((purchase, purchaseIndex) => (
                                <li key={purchaseIndex} className="purchase-item">
                                  <div className="purchase-item-content">
                                    {purchase.name}: {formatCurrency(purchase.amount)}
                                  </div>
                                  <div className="purchase-actions">
                                    <button 
                                      onClick={() => handleRemovePurchase(date, purchase.id)} 
                                      title="Remove purchase"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </li>
                              ))}
                              <li className="purchases-total">
                                Total: {formatCurrency(purchasesTotal)}
                              </li>
                            </ul>
                          ) : (
                            <span>{formatCurrency(0)}</span>
                          )}
                          <button
                            onClick={() => {
                              setNewPurchase({ ...newPurchase, date: dateStr });
                              setShowAddPurchaseDialog(true);
                            }}
                            className="add-item-button"
                          >
                            + Add Purchase
                          </button>
                        </div>
                      </div>
                      
                      <div className="calculation-summary">
                        <div className="calc-item">
                          <span>Balance Forward:</span>
                          <span>{formatCurrency((isFriday || isTuesday) ? balanceAfterPayroll : balanceForward)}</span>
                        </div>
                        <div className="calc-item">
                          <span>Total Bills:</span>
                          <span>- {formatCurrency(billsTotal)}</span>
                        </div>
                        <div className="calc-item">
                          <span>Total Purchases:</span>
                          <span>- {formatCurrency(purchasesTotal)}</span>
                        </div>
                        <div className="calc-divider"></div>
                        <div className="calc-item total">
                          <span>Total:</span>
                          <span>{formatCurrency(intermediateBalance)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Revenue Section */}
                    <div className="revenue-section">
                      <div className="section-header">
                        <h4>Revenue Calculation</h4>
                      </div>
                      
                      <div className="day-item">
                        <span>Revenue:</span>
                        {!isSunday ? (
                          <input
                            type="number"
                            value={dailyRevenue[dateStr] || ''}
                            onChange={(e) => handleDailyRevenueChange(dateStr, e.target.value)}
                            placeholder="0.00"
                          />
                        ) : (
                          <span>{formatCurrency(0)}</span>
                        )}
                      </div>
                      
                      {!isSunday && (
                        <div className="profit-tax-controls">
                          <div className="profit-control">
                            <div className="control-header">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={!customSettings.useCustomProfit}
                                  onChange={() => toggleCustomProfit(dateStr)}
                                />
                                Use Default Profit (10%)
                              </label>
                            </div>
                            
                            {customSettings.useCustomProfit && (
                              <div className="custom-control">
                                <label>
                                  Custom Profit (%):
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={customSettings.profitAmount}
                                    onChange={(e) => handleCustomProfitChange(dateStr, e.target.value)}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                          
                          <div className="tax-control">
                            <div className="control-header">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={!customSettings.useCustomTax}
                                  onChange={() => toggleCustomTax(dateStr)}
                                />
                                Use Default Tax (10%)
                              </label>
                            </div>
                            
                            {customSettings.useCustomTax && (
                              <div className="custom-control">
                                <label>
                                  Custom Tax (%):
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={customSettings.taxAmount}
                                    onChange={(e) => handleCustomTaxChange(dateStr, e.target.value)}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="calculation-summary">
                        <div className="calc-item">
                          <span>Revenue:</span>
                          <span>{formatCurrency(revenue)}</span>
                        </div>
                        <div className="calc-item">
                          <span>Profit ({customSettings.useCustomProfit ? `${customSettings.profitAmount}%` : '10%'}):</span>
                          <span>- {formatCurrency(profit)}</span>
                        </div>
                        <div className="calc-item">
                          <span>Taxes ({customSettings.useCustomTax ? `${customSettings.taxAmount}%` : '10%'}):</span>
                          <span>- {formatCurrency(taxes)}</span>
                        </div>
                        <div className="calc-divider"></div>
                        <div className="calc-item total">
                          <span>Total:</span>
                          <span>{formatCurrency(revenue - profit - taxes)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* New Balance Forward */}
                    <div className="new-balance-section">
                      <div className="day-item new-balance-forward">
                        <span>New Balance Forward:</span>
                        <span>{formatCurrency(newBalanceForward)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="print-button">
        <button onClick={() => window.print()}>
          Print Planner
        </button>
      </div>
    </div>
  );
};

export default WeeklyBillsPlanner; 